import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BRANCH_LABELS, type ReorderSuggestion } from '@lorsamthai/ops-shared';
import { dismissSuggestion, listSuggestions } from '../lib/queries.js';
import { Badge } from '../ui/Badge.js';
import { fmtDate, fmtInt } from '../lib/format.js';

const ACTION_TONE: Record<string, 'danger' | 'warn' | 'info' | 'ok' | 'neutral'> = {
  urgent_transfer: 'danger',
  purchase: 'warn',
  split: 'warn',
  transfer: 'info',
  watch: 'neutral',
  skip: 'neutral',
};

const ACTION_LABEL: Record<string, string> = {
  urgent_transfer: 'โอนด่วน',
  purchase: 'สั่งซื้อ',
  split: 'โอน + สั่ง',
  transfer: 'โอนจาก bp',
  watch: 'รอดู',
  skip: 'ข้าม',
};

export default function Suggestions() {
  const [items, setItems] = useState<ReorderSuggestion[]>([]);
  const [date, setDate] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await listSuggestions();
      setItems(r.items);
      setDate(r.date);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const dismiss = async (id: number) => {
    await dismissSuggestion(id);
    await load();
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold">Suggestions</h1>
        <p className="mt-1 text-sm text-slate-500">
          ระบบแนะนำจาก cron รอบล่าสุด {date ? `(${fmtDate(date)})` : ''}
        </p>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">กำลังโหลด...</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
          ยังไม่มี suggestions — ลองรัน <code className="rounded bg-slate-100 px-1">run_nightly</code> จากหน้าหลัก
          หรือรอ cron ถัดไป (จ/พ/ศ 19:00)
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">SKU</th>
                <th className="px-3 py-2">สาขา</th>
                <th className="px-3 py-2 text-right">on_hand</th>
                <th className="px-3 py-2 text-right">incoming</th>
                <th className="px-3 py-2 text-right">effective</th>
                <th
                  className="px-3 py-2 text-right"
                  title="นับเฉพาะยาง DOT ปีปัจจุบัน — qty คิดจากตัวเลขนี้"
                >
                  DOT ปีนี้
                </th>
                <th className="px-3 py-2 text-right">min/target</th>
                <th className="px-3 py-2 text-right">days cover</th>
                <th className="px-3 py-2">แนะนำ</th>
                <th className="px-3 py-2 text-right">qty</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((s) => (
                <tr key={s.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono text-xs">
                    <Link to={`/stock/${encodeURIComponent(s.sku)}`} className="hover:underline">
                      {s.sku}
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-xs">{BRANCH_LABELS[s.branch]}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtInt(s.on_hand)}</td>
                  <td className="px-3 py-2 text-right font-mono text-blue-600">{fmtInt(s.incoming_po)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtInt(s.effective_stock)}</td>
                  <td className="px-3 py-2 text-right font-mono font-semibold">
                    {s.effective_cy !== null ? fmtInt(s.effective_cy) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs text-slate-500">
                    {fmtInt(s.min_qty)}/{fmtInt(s.target_qty)}
                  </td>
                  <td className="px-3 py-2 text-right text-xs">
                    {s.days_of_cover !== null ? s.days_of_cover.toFixed(1) : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <Badge tone={ACTION_TONE[s.action] ?? 'neutral'}>
                      {ACTION_LABEL[s.action] ?? s.action}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {s.action === 'split' ? (
                      <span>
                        <span className="text-blue-600">โอน {s.transfer_qty}</span>
                        {' + '}
                        <span className="text-amber-600">สั่ง {s.purchase_qty}</span>
                      </span>
                    ) : s.action === 'transfer' || s.action === 'urgent_transfer' ? (
                      <span className="text-blue-600">{s.transfer_qty}</span>
                    ) : s.action === 'purchase' ? (
                      <span className="text-amber-600">{s.purchase_qty}</span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => dismiss(s.id)}
                      className="text-xs text-slate-400 hover:text-red-500"
                      title="ปัดทิ้ง"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
